import App from 'next/app'
import React from 'react'
import pclnExpt from '@pcln/experimentation'
import { ThemeProvider } from 'pcln-design-system'
import { tag } from '@pcln/measure'
import { brandRefreshTheme } from '@pcln/brand'
import BootstrapDataContext from '@/context/BootstrapDataContext'
import ExperimentContext from '@/context/ExperimentContext'
import {
  BaseSetiResponseEvent,
  BootstrapData,
  Referral,
  Webstats
} from '@/types'
import GlobalStyle from '@/components/GlobalStyles.styled'

const PRODUCT_CODES = {
  flights: 1,
  hotels: 5,
  packages: 17,
  home: 16
} as { [key: string]: number }

/**
 *
 * @param {Array<Object>} fullSETI - full list of seti tests being run
 * @param {boolean} isMobile - whether a desktop or mobile browser
 * @param {string} key - product name i.e. packages, hotels, etc
 * @param {Object} referral - traffic info
 * @param {Object} webstats - client info, cguid, user agent..
 */
function initializePclnExpt({
  fullSETI = [],
  isMobile = false,
  key: productCode = '',
  referral,
  webstats
}: {
  fullSETI: BaseSetiResponseEvent[]
  isMobile: boolean
  key: string
  referral: Referral
  webstats: Webstats
}): void {
  const webAppMetadata = {
    appCode: isMobile ? 'MOBILEWEB' : 'DESKTOPWEB',
    clientIP: webstats.resolvedClientIP,
    globalPartnerCode: 'PCLN',
    partnerCode: 'pcln',
    productID: PRODUCT_CODES[productCode],
    productSubType: 'ALL',
    referralClickID: referral.clickId,
    referralID: referral.id,
    referralSourceID: referral.sourceId,
    serverTimestamp: webstats.requestTime
  }

  if (productCode !== '') {
    pclnExpt.initialize(fullSETI, webAppMetadata, {
      useBatching: true,
      timeOutForApiCalls: 8000 // override the default of 5000
    })
  }
}

export default class MyApp extends App<any, Record<string, boolean>> {
  clearBatchHandler = () => {
    const bootstrapData = this.props.pageProps?.bootstrapData as
      | BootstrapData
      | undefined
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (bootstrapData?.key !== '') {
      pclnExpt.clearBatch()
    }
  }

  componentDidMount() {
    const bootstrapData = this.props.pageProps?.bootstrapData as
      | BootstrapData
      | undefined
    if (bootstrapData) {
      initializePclnExpt(bootstrapData)
      window.addEventListener('beforeunload', this.clearBatchHandler)
    }
  }

  componentWillUnmount() {
    const bootstrapData = this.props.pageProps?.bootstrapData as
      | BootstrapData
      | undefined
    if (bootstrapData) {
      window.removeEventListener('beforeunload', this.clearBatchHandler)
    }
  }

  render() {
    const { Component, pageProps } = this.props
    const bootstrapData = pageProps?.bootstrapData as BootstrapData | undefined

    // this will add a tag to the boomerang events with the appName and appVersion
    if (typeof window !== 'undefined' && bootstrapData) {
      tag('appName', bootstrapData.appName || 'next-landing')
      tag('appVersion', bootstrapData.appVersion)
    }

    return (
      <ThemeProvider theme={brandRefreshTheme}>
        <GlobalStyle />
        <BootstrapDataContext.Provider
          value={(bootstrapData ?? {}) as BootstrapData}
        >
          <ExperimentContext.Provider value={bootstrapData?.seti ?? {}}>
            <Component {...pageProps} />
          </ExperimentContext.Provider>
        </BootstrapDataContext.Provider>
      </ThemeProvider>
    )
  }
}
